<nz-card nzTitle="Anyo Goals" [nzLoading]="loading">
  <ng-container *ngIf="!loading">
    <div *ngIf="loadError" class="text-red-500 mb-2">
      <ng-container *ngIf="view?.goal; else noStaleData">
        Failed to load latest goal details. Data shown below may be stale.
      </ng-container>
      <ng-template #noStaleData>
        Failed to load goal details for this user. Please retry the search.
      </ng-template>
    </div>

    <div *ngIf="!loadError && !view?.goal" class="text-gray-500">
      This user has no goal history yet.
    </div>

    <ng-container *ngIf="view?.goal as goal">
      <div class="flex items-center gap-3 mb-3">
        <nz-tag
          [nzColor]="
            goal.status === GoalStatus.ACTIVE
              ? 'green'
              : goal.status === GoalStatus.PAUSED
              ? 'orange'
              : goal.status === GoalStatus.ABANDONED
              ? 'red'
              : 'default'
          "
          >{{ goal.status }}</nz-tag
        >
        <span *ngIf="goal.heroPillar">Hero pillar: {{ goal.heroPillar }}</span>
        <span>Started: {{ goal.startDate | date: 'medium' }}</span>
        <span *ngIf="goal.pausedAt">Paused since: {{ goal.pausedAt | date: 'medium' }}</span>
      </div>

      <div class="mb-3" *ngIf="goal.abandonReason">
        <b>Abandon reason:</b> {{ goal.abandonReason }}
      </div>

      <div class="mb-3" *ngIf="goal.audit?.rationale">
        <b>Why this goal was set:</b>
        <p>{{ goal.audit.rationale }}</p>
      </div>

      <div class="flex gap-2 mb-4">
        <button
          nz-button
          nzType="default"
          [disabled]="!canPause() || actionLoading"
          (click)="pauseGoal()"
        >
          Pause
        </button>
        <button
          nz-button
          nzType="default"
          [disabled]="!canResume() || actionLoading"
          (click)="resumeGoal()"
        >
          Resume
        </button>
        <button
          nz-button
          nzType="default"
          nzDanger
          [disabled]="!canAbandonOrChangeExpert() || actionLoading"
          (click)="openAbandonModal()"
        >
          Abandon
        </button>
        <button
          nz-button
          nzType="default"
          [disabled]="!canAbandonOrChangeExpert() || actionLoading"
          (click)="openChangeExpertModal()"
        >
          Change Expert
        </button>
      </div>

      <nz-collapse>
        <nz-collapse-panel nzHeader="Status history" [nzActive]="false">
          <nz-table
            #statusHistoryTable
            [nzData]="goal.statusHistory"
            [nzShowPagination]="false"
            [nzFrontPagination]="false"
          >
            <thead>
              <tr>
                <th>Changed At</th>
                <th>From</th>
                <th>To</th>
                <th>Reason</th>
                <th>Actor</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let entry of goal.statusHistory">
                <td>{{ entry.changedAt | date: 'medium' }}</td>
                <td>{{ entry.fromStatus }}</td>
                <td>{{ entry.toStatus }}</td>
                <td>{{ entry.reason }}</td>
                <td>{{ entry.actor }}</td>
              </tr>
            </tbody>
          </nz-table>
          <div *ngIf="!goal.statusHistory?.length" class="text-gray-500">
            No status changes recorded yet.
          </div>
        </nz-collapse-panel>

        <nz-collapse-panel nzHeader="Objectives" [nzActive]="true">
          <div *ngIf="!view?.objectives?.length" class="text-gray-500">
            No objectives on this goal.
          </div>
          <nz-card
            *ngFor="let obj of view?.objectives"
            [nzTitle]="obj.displayTitle || obj.objectiveId"
            class="mb-3"
          >
            <div class="flex gap-2 mb-2">
              <nz-tag>{{ obj.status }}</nz-tag>
              <nz-tag nzColor="red" *ngIf="obj.planProgressorState?.needsGoalAgentHandback"
                >Handback needed</nz-tag
              >
              <nz-tag nzColor="red" *ngIf="obj.planProgressorState?.terminalOptionsNeeded"
                >Terminal options needed</nz-tag
              >
              <nz-tag nzColor="orange" *ngIf="obj.planProgressorState?.disengagementMenuNeeded"
                >Disengaged</nz-tag
              >
              <nz-tag nzColor="orange" *ngIf="obj.planProgressorState?.rebaselineOfferNeeded"
                >Rebaseline offer needed</nz-tag
              >
              <nz-tag nzColor="blue" *ngIf="obj.planProgressorState?.dormancyFrozenAt"
                >Dormant</nz-tag
              >
              <nz-tag nzColor="magenta" *ngIf="obj.planProgressorState?.rampWithoutDestinationFlagged"
                >Ramp without destination (authoring gap)</nz-tag
              >
              <button
                *ngIf="obj.status === 'active'"
                nz-button
                nzSize="small"
                (click)="openMarkDayCompleteModal(obj.userObjectiveId)"
              >
                Mark day complete
              </button>
            </div>
            <div *ngIf="obj.configHistory?.length">
              <b>Latest change:</b>
              {{ obj.configHistory[obj.configHistory.length - 1].reason }}
              <span *ngIf="obj.configHistory[obj.configHistory.length - 1].holdReason">
                (held: {{ obj.configHistory[obj.configHistory.length - 1].holdReason }})
              </span>
            </div>
            <div *ngIf="obj.manualDayOverrides?.length" class="mt-2">
              <b>Manually marked complete:</b>
              <nz-table
                [nzData]="obj.manualDayOverrides"
                [nzShowPagination]="false"
                [nzFrontPagination]="false"
                nzSize="small"
              >
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Marked By</th>
                    <th>Reason</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let override of obj.manualDayOverrides">
                    <td>{{ override.date }}</td>
                    <td>{{ override.markedBy }}</td>
                    <td>{{ override.overrideReason }}</td>
                    <td>{{ override.computedAt | date: 'medium' }}</td>
                  </tr>
                </tbody>
              </nz-table>
            </div>
          </nz-card>
        </nz-collapse-panel>
      </nz-collapse>
    </ng-container>
  </ng-container>
</nz-card>

<nz-modal
  [(nzVisible)]="isAbandonModalOpen"
  nzTitle="Abandon this goal?"
  (nzOnCancel)="isAbandonModalOpen = false"
  (nzOnOk)="confirmAbandon()"
  [nzOkLoading]="actionLoading"
>
  <ng-container *nzModalContent>
    <p>This is a terminal action — the goal cannot be resumed afterward.</p>
    <textarea
      nz-input
      [(ngModel)]="abandonReason"
      placeholder="Reason (optional)"
      rows="3"
    ></textarea>
  </ng-container>
</nz-modal>

<nz-modal
  [(nzVisible)]="isChangeExpertModalOpen"
  nzTitle="Change assigned expert"
  (nzOnCancel)="isChangeExpertModalOpen = false"
  (nzOnOk)="confirmChangeExpert()"
  [nzOkLoading]="actionLoading"
  [nzOkDisabled]="!selectedNewExpertId"
>
  <ng-container *nzModalContent>
    <nz-select
      [(ngModel)]="selectedNewExpertId"
      [nzLoading]="expertOptionsLoading"
      nzPlaceHolder="Select a Goals expert"
      style="width: 100%"
    >
      <nz-option
        *ngFor="let expert of expertOptions"
        [nzValue]="expert.id"
        [nzLabel]="expert.name"
      ></nz-option>
    </nz-select>
  </ng-container>
</nz-modal>

<nz-modal
  [(nzVisible)]="isMarkDayCompleteModalOpen"
  nzTitle="Mark day complete"
  (nzOnCancel)="isMarkDayCompleteModalOpen = false"
  (nzOnOk)="confirmMarkDayComplete()"
  [nzOkLoading]="actionLoading"
  [nzOkDisabled]="!markDayCompleteDate || !markDayCompleteReason"
>
  <ng-container *nzModalContent>
    <p>
      Support override — bypasses real signal data, becomes part of the audit
      trail (visible under "Manually marked complete" below).
    </p>
    <nz-date-picker
      [(ngModel)]="markDayCompleteDate"
      [nzDisabledDate]="isFutureDate"
      style="width: 100%; margin-bottom: 12px"
    ></nz-date-picker>
    <textarea
      nz-input
      [(ngModel)]="markDayCompleteReason"
      placeholder="Why? (required, becomes part of the audit trail)"
      rows="3"
    ></textarea>
  </ng-container>
</nz-modal>
